import { defineStore } from 'pinia'
import Decimal from 'decimal.js-light'
import { useQueryCache } from '@pinia/colada'
import type { UseInfiniteQueryData } from '@pinia/colada'
import { TradeHistoryModel } from '@trading/core'
import type { TradeHistoryTrade } from '@trading/core'
import type { TradeWSPayload, TradeWSUpdate } from '@trading/infrastructure'
import { TRADE_HISTORY_QUERY_KEY, getTradeHistoryQueryKey } from '~/queries/tradeHistory'
import type { UnixDateRange } from '~/components/features/TradeHistoryTable/utils/dateRange'

type TradeHistoryPages = UseInfiniteQueryData<TradeHistoryModel, string | null>

function toHistoryTrade(trade: TradeWSPayload): TradeHistoryTrade {
  return {
    account_id: trade.account_id,
    id: trade.id,
    symbol: trade.symbol,
    side: trade.side,
    price: trade.price,
    quantity: trade.quantity,
    fee: trade.fee,
    fee_currency: trade.fee_currency,
    is_maker: trade.is_maker,
    realized_pnl: trade.realized_pnl,
    trade_time: trade.trade_time,
    open_time: trade.open_time,
    close_time: trade.close_time,
    swap: trade.swap,
    sl: trade.sl ?? '0',
    tp: trade.tp ?? '0'
  }
}

function addAmount(value: string, delta: string): string {
  return new Decimal(value || '0').add(delta || '0').toString()
}

function isInRange(timestamp: number, range: UnixDateRange | null): boolean {
  if (!range) {
    return true
  }

  return timestamp >= range.from && timestamp <= range.to
}

function isVisibleInRanges(
  trade: TradeWSPayload,
  openDateRange: UnixDateRange | null,
  closeDateRange: UnixDateRange | null
): boolean {
  const tradeTime = Math.floor(Date.parse(trade.trade_time) / 1000)

  return isInRange(tradeTime, openDateRange) && isInRange(tradeTime, closeDateRange)
}

function withTradeCounted(page: TradeHistoryModel, trade: TradeWSPayload): TradeHistoryModel {
  const summary = {
    swap: addAmount(page.summary.swap, trade.swap),
    commission: addAmount(page.summary.commission, trade.commission),
    pnl: addAmount(page.summary.pnl, trade.realized_pnl)
  }

  return new TradeHistoryModel(page.trades, summary, page.nextCursor)
}

function withTradeUncounted(page: TradeHistoryModel, trade: TradeWSPayload): TradeHistoryModel {
  const summary = {
    swap: addAmount(page.summary.swap, `-${trade.swap || '0'}`),
    commission: addAmount(page.summary.commission, `-${trade.commission || '0'}`),
    pnl: addAmount(page.summary.pnl, `-${trade.realized_pnl || '0'}`)
  }

  return new TradeHistoryModel(
    page.trades.filter((item) => String(item.id) !== String(trade.id)),
    summary,
    page.nextCursor
  )
}

export const useTradeHistoryStore = defineStore('tradeHistory', () => {
  const queryCache = useQueryCache()
  const tradeHistoryTableStore = useTradeHistoryTableStore()

  function getWsTradeHistoryService() {
    return useNuxtApp().$wsTradeHistoryService
  }

  function prependTrade(payload: TradeWSPayload) {
    const { openDateRange, closeDateRange } = tradeHistoryTableStore
    const key = getTradeHistoryQueryKey(payload.account_id, openDateRange, closeDateRange)
    const visibleInRanges = isVisibleInRanges(payload, openDateRange, closeDateRange)

    if (!visibleInRanges) {
      console.warn('[TradeHistory WS] skipped: trade is outside date ranges')
      return
    }

    const data = queryCache.getQueryData<TradeHistoryPages>(key)

    if (!data?.pages.length) {
      console.warn('[TradeHistory WS] skipped: no cached query data for key', key)
      return
    }

    const [firstPage, ...restPages] = data.pages
    if (!firstPage) {
      console.warn('[TradeHistory WS] skipped: first page is missing')
      return
    }

    if (firstPage.trades.some((item) => item.id === payload.id)) {
      console.warn('[TradeHistory WS] skipped: trade already exists', payload.id)
      return
    }

    // Не даём уже запущенному initial fetch/refetch завершиться старым ответом
    // поверх данных, добавленных из WebSocket.
    console.log('[TradeHistory WS] cancelling active query before cache update')
    queryCache.cancelQueries({ key })

    const tradeOnTop = new TradeHistoryModel(
      [toHistoryTrade(payload), ...firstPage.trades],
      firstPage.summary,
      firstPage.nextCursor
    )

    const pages = [tradeOnTop, ...restPages].map((page) => withTradeCounted(page, payload))

    queryCache.setQueryData<TradeHistoryPages>(key, { pages, pageParams: data.pageParams })
  }

  function removeTrade(payload: TradeWSPayload) {
    const { openDateRange, closeDateRange } = tradeHistoryTableStore
    const key = getTradeHistoryQueryKey(payload.account_id, openDateRange, closeDateRange)
    const data = queryCache.getQueryData<TradeHistoryPages>(key)

    if (!data?.pages.length) {
      console.warn('[TradeHistory WS] skipped: no cached query data for key', key)
      return
    }

    const tradeExists = data.pages.some((page) =>
      page.trades.some((item) => String(item.id) === String(payload.id))
    )

    if (!tradeExists) {
      console.warn('[TradeHistory WS] skipped: trade does not exist', payload.id)
      return
    }

    // Не даём уже запущенному initial fetch/refetch завершиться старым ответом
    // поверх данных после удаления сделки из WebSocket.
    console.log('[TradeHistory WS] cancelling active query before cache update')
    queryCache.cancelQueries({ key })

    const pages = data.pages.map((page) => withTradeUncounted(page, payload))
    queryCache.setQueryData<TradeHistoryPages>(key, { pages, pageParams: data.pageParams })
  }

  async function subscribeWs() {
    return getWsTradeHistoryService().subscribeToTradesChannel('update', (data: TradeWSUpdate) => {
      if (data.event === 'created') {
        prependTrade(data.trade)
      } else if (data.event === 'deleted') {
        removeTrade(data.trade)
      }
    })
  }

  function unsubscribe(): void {
    getWsTradeHistoryService().unsubscribe()
  }

  function reset() {
    queryCache.cancelQueries({
      key: [TRADE_HISTORY_QUERY_KEY]
    })

    queryCache
      .getEntries({
        key: [TRADE_HISTORY_QUERY_KEY]
      })
      .forEach((entry) => {
        queryCache.remove(entry)
      })
  }

  return {
    prependTrade,
    removeTrade,
    subscribeWs,
    unsubscribe,
    reset
  }
})
